import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { CoreDataService } from '../core.data';
import { CustomEncoder } from '../http.util';
import { VersionConfigService } from '@app/core/services/version-config';
import { Observable } from 'rxjs';
import {
  ServiceMap,
  ServiceMapForUserPayload,
} from './interfaces/permissions.interface';
import { API } from '@app/core/models';
import { ApiVersionedRoute } from './api-versions';
import { map } from 'rxjs/operators';
import { HttpOptions } from '@app/core/models/api.interface';

@Injectable({
  providedIn: 'root',
})
export class PermissionsHttpService {
  private _versionBasedUrlPrefix = '';

  constructor(
    private _platform: Platform,
    private _coreDataService: CoreDataService,
    private _versionConfigService: VersionConfigService,
  ) {
    this._versionConfigService.getVersion('permissions').subscribe((prefix) => {
      this._versionBasedUrlPrefix = `${prefix}/permissions/`;
    });
  }

  getPermissions(webUserId: string) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    }).set('id', webUserId);

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}`,
      options,
    );
  }

  getUserPermissions(webUserId: string) {
    const headers = new HttpHeaders({
      /* eslint-disable @typescript-eslint/naming-convention */
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      /* eslint-disable @typescript-eslint/naming-convention */
    });
    let options;

    if (!this._platform.is('capacitor')) {
      options = {
        headers,
        withCredentials: true,
        params: null,
      } as HttpOptions;
      options.params = new HttpParams({
        encoder: new CustomEncoder(),
      }).set('id', webUserId);
    } else {
      options = {
        headers,
        params: {
          id: webUserId,
        },
      };
    }

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}user`,
      options,
    );
  }

  saveUserPermissions(webUserId: string, formObject: any) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };
    const body = {
      WebUserId: webUserId,
      optionGuids: formObject.formPermissionsControl || [],
      departmentFilters: formObject.formDepartmentFiltersControl || [],
      customPositionGroups: formObject.formPositionGroupsControl || [],
      timeAndAttendance: {
        filters: formObject.timeAndAttendanceFiltersAssigned || [],
      },
    };

    return this._coreDataService.post<any>(
      `${this._versionBasedUrlPrefix}user`,
      body,
      options,
    );
  }

  getComponentPermissions() {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}component`,
      options,
    );
  }

  getCustomPositionGroups(webUserId: string) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    }).set('id', webUserId);

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}position/groups`,
      options,
    );
  }

  getDepartmentFilters(webUserId: string) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    }).set('id', webUserId);

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}department/filters`,
      options,
    );
  }

  getTimeAndAttendanceFilters(
    webUserId: string,
    timeAndAttendanceLevel: string,
  ) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    })
      .set('id', webUserId)
      .set('timeAndAttendanceLevel', timeAndAttendanceLevel);

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}timeandattendance/filters`,
      options,
    );
  }

  getRecommendedOptionsForUserType(userTypeId: string) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = new HttpParams({
      encoder: new CustomEncoder(),
    }).set('userTypeId', userTypeId);

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}recommended`,
      options,
    );
  }

  getServiceMapForUser(): Observable<ServiceMap> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .get<API.Response<ServiceMapForUserPayload>>(
        `${ApiVersionedRoute.permissions}/service-map-for-user`,
        options,
      )
      .pipe(map((response) => response.payload?.serviceMap as ServiceMap));
  }
}
